// =============================================================================
// ImportTemplatesPanel.jsx
//
// Settings tab: one Template per Side (Cell_Owner) for the Bulk Import feature.
// Lets Admin/Manager upload a sample file (to read its column headers), map
// Tracker fields (Title/Vendor/Technology/Comments/JV_ID) to those columns,
// set value aliases (e.g. "LTE" -> "4G"), and pick a default Feature.
// =============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, IconButton, Tooltip, TextField, Switch,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  MenuItem, Select, InputLabel, FormControl, OutlinedInput, Chip,
  CircularProgress, Alert, Divider,
} from '@mui/material';
import AddIcon           from '@mui/icons-material/Add';
import EditIcon          from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowBackIcon     from '@mui/icons-material/ArrowBack';
import UploadFileIcon    from '@mui/icons-material/UploadFile';
import InfoOutlinedIcon  from '@mui/icons-material/InfoOutlined';
import {
  apiGetImportTemplates, apiSaveImportTemplate, apiDeleteImportTemplate, apiReadImportHeaders,
} from '../requests/importRequests.js';
import { apiGetFeatures } from '../requests/trackerRequests.js';

const cellSx = { fontSize: '0.78rem', py: 0.75 };
const headSx = {
  fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.8,
  color: 'rgba(255,255,255,0.85)', bgcolor: 'primary.main', py: 1, borderBottom: 'none',
};

// Fixed set of Tracker fields the Bulk Import can populate. JvId isn't a
// TrackerEntry field itself — it's the column used to resolve CSR/TAC/OMC_ID.
const TARGET_FIELDS = [
  { key: 'JvId', label: 'JV_ID column', hint: 'Column that holds the JV_ID used to resolve sites.' },
  { key: 'Title', label: 'Title', hint: 'Usually one column (e.g. DESCRIPTION).' },
  { key: 'Vendor', label: 'Vendor', hint: 'Column with the vendor name (e.g. VENDOR).' },
  { key: 'Technology', label: 'Technology', hint: 'Column with the technology (e.g. TECHNOLOGY).' },
  { key: 'Comments', label: 'Comments', hint: 'One or more columns, combined and de-duplicated across all rows.' },
];

const TRANSFORMS = [
  { key: 'singleColumn', label: 'Single column' },
  { key: 'direct', label: 'Direct (1:1)' },
  { key: 'concatDistinct', label: 'Concatenate + distinct' },
  { key: 'groupByCount', label: 'Group By + Count' },
];

// Transforms that let the admin write a free-text Template (with
// {ColumnName} placeholders) instead of just joining raw values.
const TEMPLATE_TRANSFORMS = ['concatDistinct', 'groupByCount'];

const ALIAS_TYPES = ['Technology', 'Vendor'];

const defaultMappingFor = (fieldKey) => ({
  targetField: fieldKey,
  sourceColumns: [],
  transform: fieldKey === 'Comments' ? 'concatDistinct' : 'singleColumn',
  separator: ', ',
  template: '',
  sortOrder: 0,
});

const emptyTemplate = () => ({
  id: null,
  side: '',
  name: '',
  expectedHeaders: [],
  defaultFeatureId: '',
  isActive: true,
  sortOrder: 0,
  mappings: TARGET_FIELDS.map((f) => defaultMappingFor(f.key)),
  aliases: [],
});

// Ensures every TARGET_FIELDS entry has a mapping row, even for templates
// saved before a new target field existed.
const normalizeMappings = (mappings) => TARGET_FIELDS.map((f) => {
  const existing = (mappings ?? []).find((m) => m.targetField?.toLowerCase() === f.key.toLowerCase());
  return existing ? { ...existing, sourceColumns: existing.sourceColumns ?? [] } : defaultMappingFor(f.key);
});

const ImportTemplatesPanel = () => {
  const [templates, setTemplates] = useState([]);
  const [features, setFeatures]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState(null);
  const [view, setView]           = useState('list');   // 'list' | 'editor'
  const [form, setForm]           = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    apiGetImportTemplates()
      .then((res) => setTemplates(res.data ?? []))
      .catch(() => setError('Failed to load templates.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    apiGetFeatures().then((res) => setFeatures(res.data ?? [])).catch(() => {});
  }, [load]);

  const openNew = () => { setForm(emptyTemplate()); setView('editor'); setError(null); };

  const openEdit = (t) => {
    setForm({
      ...t,
      defaultFeatureId: t.defaultFeatureId ?? '',
      expectedHeaders: t.expectedHeaders ?? [],
      mappings: normalizeMappings(t.mappings),
      aliases: t.aliases ?? [],
    });
    setView('editor');
    setError(null);
  };

  const backToList = () => { setView('list'); setForm(null); };

  const handleDelete = (id) => {
    if (!window.confirm('Delete this import template? This also removes its mapping and aliases.')) return;
    setSaving(true);
    apiDeleteImportTemplate(id)
      .then(() => load())
      .catch(() => setError('Failed to delete.'))
      .finally(() => setSaving(false));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';   // allow re-selecting the same file
    if (!file) return;

    setUploading(true);
    setError(null);
    apiReadImportHeaders(file)
      .then((res) => setForm((p) => ({ ...p, expectedHeaders: res.data ?? [] })))
      .catch(() => setError('Failed to read the file headers.'))
      .finally(() => setUploading(false));
  };

  const updateMapping = (targetField, patch) => {
    setForm((p) => ({
      ...p,
      mappings: p.mappings.map((m) => (m.targetField === targetField ? { ...m, ...patch } : m)),
    }));
  };

  const addAlias = () => {
    setForm((p) => ({
      ...p,
      aliases: [...p.aliases, { id: null, aliasType: 'Technology', rawValue: '', canonicalValue: '', isActive: true }],
    }));
  };

  const updateAlias = (idx, patch) => {
    setForm((p) => ({
      ...p,
      aliases: p.aliases.map((a, i) => (i === idx ? { ...a, ...patch } : a)),
    }));
  };

  const removeAlias = (idx) => {
    setForm((p) => ({ ...p, aliases: p.aliases.filter((_, i) => i !== idx) }));
  };

  const handleSave = () => {
    if (!form.side?.trim() || !form.name?.trim()) {
      setError('Side and Name are required.');
      return;
    }
    const jvIdMapping = form.mappings.find((m) => m.targetField === 'JvId');
    if (!jvIdMapping?.sourceColumns?.length) {
      setError('The JV_ID column mapping is required.');
      return;
    }
    const missingTemplate = form.mappings.find((m) => m.transform === 'groupByCount' && !m.template?.trim());
    if (missingTemplate) {
      setError(`"${missingTemplate.targetField}" uses Group By + Count and needs a Template.`);
      return;
    }

    setSaving(true);
    setError(null);
    apiSaveImportTemplate({
      ...form,
      defaultFeatureId: form.defaultFeatureId === '' ? null : Number(form.defaultFeatureId),
    })
      .then(() => { backToList(); load(); })
      .catch(() => setError('Failed to save the template.'))
      .finally(() => setSaving(false));
  };

  // ── List view ──────────────────────────────────────────────────────────────
  if (view === 'list') {
    return (
      <Box>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

        <TableContainer sx={{ maxHeight: 420, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={headSx}>Side</TableCell>
                <TableCell sx={headSx}>Name</TableCell>
                <TableCell sx={headSx}>Default Feature</TableCell>
                <TableCell sx={{ ...headSx, width: 80 }}>Active</TableCell>
                <TableCell sx={{ ...headSx, width: 90 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3 }}><CircularProgress size={22} /></TableCell></TableRow>
              ) : templates.length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>No import templates yet.</TableCell></TableRow>
              ) : templates.map((t) => (
                <TableRow key={t.id} sx={{ opacity: t.isActive ? 1 : 0.45 }}>
                  <TableCell sx={cellSx}>{t.side}</TableCell>
                  <TableCell sx={cellSx}>{t.name}</TableCell>
                  <TableCell sx={cellSx}>{t.defaultFeatureName || '—'}</TableCell>
                  <TableCell sx={cellSx}>
                    <Chip label={t.isActive ? 'Active' : 'Inactive'} size="small"
                      color={t.isActive ? 'success' : 'default'} sx={{ height: 18, fontSize: '0.65rem' }} />
                  </TableCell>
                  <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(t)} disabled={saving}><EditIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDelete(t.id)} disabled={saving}><DeleteOutlineIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 1.5 }}>
          <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={openNew} disabled={loading}>
            Add Template
          </Button>
        </Box>
      </Box>
    );
  }

  // ── Editor view ────────────────────────────────────────────────────────────
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton size="small" onClick={backToList}><ArrowBackIcon fontSize="small" /></IconButton>
        <Typography fontWeight={600} fontSize="0.9rem">
          {form.id ? `Edit Template — ${form.name}` : 'New Template'}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
        <TextField size="small" label="Side (Cell_Owner)" placeholder="e.g. TFinTF"
          value={form.side} onChange={(e) => setForm((p) => ({ ...p, side: e.target.value }))}
          sx={{ minWidth: 180 }} />
        <TextField size="small" label="Name" placeholder="e.g. VF CM Optimisation Request"
          value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
          sx={{ minWidth: 260 }} />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Default Feature</InputLabel>
          <Select label="Default Feature" value={form.defaultFeatureId}
            onChange={(e) => setForm((p) => ({ ...p, defaultFeatureId: e.target.value }))}>
            <MenuItem value=""><em>None</em></MenuItem>
            {features.map((f) => <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>)}
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Switch size="small" checked={form.isActive}
            onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))} />
          <Typography variant="caption">Active</Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Button component="label" variant="outlined" size="small" startIcon={<UploadFileIcon />} disabled={uploading}>
          {uploading ? 'Reading…' : 'Upload sample file (reads column headers)'}
          <input type="file" hidden accept=".csv,.xlsx" onChange={handleFileUpload} />
        </Button>
        {form.expectedHeaders.length > 0 && (
          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {form.expectedHeaders.map((h) => (
              <Chip key={h} label={h} size="small" sx={{ fontSize: '0.68rem' }} />
            ))}
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />
      <Typography fontWeight={600} fontSize="0.82rem" sx={{ mb: 1 }}>Field mapping</Typography>

      <TableContainer sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headSx}>Tracker field</TableCell>
              <TableCell sx={headSx}>Source column(s)</TableCell>
              <TableCell sx={{ ...headSx, width: 170 }}>Transform</TableCell>
              <TableCell sx={{ ...headSx, width: 100 }}>Separator</TableCell>
              <TableCell sx={{ ...headSx, width: 260 }}>Template</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {TARGET_FIELDS.map((field) => {
              const mapping = form.mappings.find((m) => m.targetField === field.key) ?? defaultMappingFor(field.key);
              const usesTemplate = TEMPLATE_TRANSFORMS.includes(mapping.transform);
              return (
                <TableRow key={field.key}>
                  <TableCell sx={cellSx}>
                    <Typography variant="body2" fontWeight={500}>{field.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{field.hint}</Typography>
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <FormControl size="small" fullWidth sx={{ minWidth: 220 }}>
                      <Select
                        multiple
                        displayEmpty
                        value={mapping.sourceColumns}
                        onChange={(e) => updateMapping(field.key, {
                          sourceColumns: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value,
                        })}
                        input={<OutlinedInput />}
                        renderValue={(selected) => (selected.length ? selected.join(', ') : <em>Select column(s)…</em>)}
                      >
                        {form.expectedHeaders.map((h) => (
                          <MenuItem key={h} value={h}>{h}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <FormControl size="small" fullWidth>
                      <Select value={mapping.transform} onChange={(e) => updateMapping(field.key, { transform: e.target.value })}>
                        {TRANSFORMS.map((t) => <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <TextField
                      size="small" fullWidth
                      value={mapping.separator ?? ''}
                      disabled={!usesTemplate}
                      placeholder={usesTemplate ? 'newline' : ''}
                      onChange={(e) => updateMapping(field.key, { separator: e.target.value })}
                    />
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                      <TextField
                        size="small" fullWidth multiline maxRows={3}
                        value={mapping.template ?? ''}
                        disabled={!usesTemplate}
                        placeholder={
                          mapping.transform === 'groupByCount'
                            ? 'e.g. {Vendor} {MO_NAME}.{PARAMETER_NAME} ({EXISTING_PARAM_VALUE} → {NEW_PARAM_VALUE}): {Count}'
                            : 'Optional — leave empty to just join the raw values'
                        }
                        onChange={(e) => updateMapping(field.key, { template: e.target.value })}
                      />
                      {usesTemplate && (
                        <Tooltip
                          arrow
                          placement="top-end"
                          title={
                            <Box sx={{ maxWidth: 320, p: 0.5 }}>
                              <Typography variant="caption" sx={{ display: 'block', color: 'inherit', mb: mapping.sourceColumns?.length ? 0.5 : 0 }}>
                                Write the exact text you want in Comments. Use <strong>{'{ColumnName}'}</strong> to insert
                                the value of any column selected on the left
                                {mapping.transform === 'groupByCount'
                                  ? <> , and <strong>{'{Count}'}</strong> for how many rows matched that combination.</>
                                  : '.'}
                                {' '}Rows are grouped by the exact combination of the selected columns
                                {mapping.transform === 'groupByCount' ? '.' : ' — one line per distinct combination found.'}
                              </Typography>
                              {mapping.sourceColumns?.length > 0 && (
                                <Typography variant="caption" sx={{ display: 'block', color: 'inherit', fontFamily: 'monospace' }}>
                                  Available: {mapping.sourceColumns.map((c) => `{${c}}`).join(' ')}
                                  {mapping.transform === 'groupByCount' ? ' {Count}' : ''}
                                </Typography>
                              )}
                            </Box>
                          }
                          // A tabela deste Field Mapping tem scroll horizontal próprio
                          // (TableContainer), e o Popper por omissão usa o "clipping
                          // parent" mais próximo (essa scrollbar) para decidir onde
                          // cabe o tooltip — por isso força-se a boundary para a
                          // janela. Além disso, o fundo/cor do balão do tooltip são
                          // fixados explicitamente aqui (em vez de herdar do tema) —
                          // era isso que ficava ilegível: texto e fundo claros
                          // quase iguais, com o conteúdo da página a transparecer.
                          slotProps={{
                            popper: {
                              modifiers: [
                                { name: 'preventOverflow', options: { boundary: 'window' } },
                                { name: 'flip', options: { boundary: 'window' } },
                              ],
                            },
                            tooltip: {
                              sx: {
                                bgcolor: '#1a1a1a',
                                color: '#fff',
                                opacity: 1,
                                boxShadow: 3,
                                fontSize: '0.75rem',
                                '& strong': { color: '#fff' },
                              },
                            },
                            arrow: { sx: { color: '#1a1a1a' } },
                          }}
                        >
                          <IconButton size="small" sx={{ p: 0.25, mt: 0.5 }}>
                            <InfoOutlinedIcon sx={{ fontSize: 16 }} color="action" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography fontWeight={600} fontSize="0.82rem">
          Value aliases <Typography component="span" variant="caption" color="text.secondary">(e.g. "LTE" → "4G")</Typography>
        </Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={addAlias}>Add alias</Button>
      </Box>

      {form.aliases.length === 0 ? (
        <Typography variant="caption" color="text.secondary">No aliases configured — file values must already match the network database exactly (case-insensitive).</Typography>
      ) : (
        <TableContainer sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headSx, width: 140 }}>Type</TableCell>
                <TableCell sx={headSx}>Raw value (in file)</TableCell>
                <TableCell sx={headSx}>Canonical value (in DB)</TableCell>
                <TableCell sx={{ ...headSx, width: 60 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {form.aliases.map((a, idx) => (
                <TableRow key={idx}>
                  <TableCell sx={cellSx}>
                    <FormControl size="small" fullWidth>
                      <Select value={a.aliasType} onChange={(e) => updateAlias(idx, { aliasType: e.target.value })}>
                        {ALIAS_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <TextField size="small" placeholder="e.g. LTE" value={a.rawValue}
                      onChange={(e) => updateAlias(idx, { rawValue: e.target.value })} fullWidth />
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <TextField size="small" placeholder="e.g. 4G" value={a.canonicalValue}
                      onChange={(e) => updateAlias(idx, { canonicalValue: e.target.value })} fullWidth />
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <IconButton size="small" color="error" onClick={() => removeAlias(idx)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <Button variant="contained" size="small" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Template'}
        </Button>
        <Button variant="text" size="small" onClick={backToList} disabled={saving}>Cancel</Button>
      </Box>
    </Box>
  );
};

export default ImportTemplatesPanel;
